// Party spellbook: every spell on each party member's D&D Beyond record (as
// the live poll reports it), matched to the inventory's handler and to the
// rule cards the DM Screen runs today. Nothing here is editable — the sheet
// is the source, and the gate: only these spells ever drive a handler.
import { useMemo } from "react";
import { useStore } from "../state/store";
import { campaignRoster } from "../state/campaign";
import { HANDLER_LABEL, ruleCardFor, spellInfo, type BookSpell, type Handler } from "../state/spellRules";

const LEVEL = (n: number) => (n === 0 ? "Cantrips" : `Level ${n}`);

export function Spellbook() {
  const { layout } = useStore();
  const roster = campaignRoster(layout).filter((c) => c.id.startsWith("ddb-"));
  // Casters first (party order kept among them); characters with no spells at the end.
  const rows = useMemo(() => roster.map((c) => ({ c, book: ((c.spellbook as BookSpell[] | undefined) || []).slice().sort((a, b) => a.level - b.level || a.name.localeCompare(b.name)) })).sort((a, b) => Number(!a.book.length) - Number(!b.book.length)), [roster]);
  const totals = useMemo(() => {
    const t = { spells: 0, withCard: 0, byHandler: {} as Record<string, number> };
    const seen = new Set<string>();
    for (const { book } of rows) for (const s of book) { const k = s.name.toLowerCase(); if (seen.has(k)) continue; seen.add(k); t.spells += 1; if (ruleCardFor(s.name)) t.withCard += 1; const h = spellInfo(s.name)?.handler || "unknown"; t.byHandler[h] = (t.byHandler[h] || 0) + 1; }
    return t;
  }, [rows]);
  return (
    <section className="panel spellbook">
      <header><div><span className="eyebrow">Party spellbook</span><h2>What the party can cast</h2></div>
        <p className="muted small">Read from each character's D&D Beyond record by the live poll — D&D Beyond's ownership and campaign-sharing rules decide what appears here, and only these spells ever drive an automation. {totals.spells} distinct spells, {totals.withCard} with a rule card today.</p>
      </header>
      {!rows.some((r) => r.book.length) && <p className="muted">No spellbooks yet — the connector reports them on the first live read (about 30 seconds after this page opens, with the connector on 0.8.168 or later).</p>}
      <div className="spellbook-grid">
        {rows.map(({ c, book }) => (
          <article key={c.id} className="card">
            <header><h3>{c.name}</h3><span className="muted small">{book.length} spell{book.length === 1 ? "" : "s"}{c.spellSaveDc ? ` · save DC ${c.spellSaveDc}` : ""}</span></header>
            {!book.length && <p className="muted small">Nothing on the sheet (or not read yet).</p>}
            {Array.from(new Set(book.map((s) => s.level))).map((level) => (
              <div key={level} className="spell-level">
                <span className="eyebrow">{LEVEL(level)}</span>
                <ul className="spell-list">
                  {book.filter((s) => s.level === level).map((s) => {
                    const info = spellInfo(s.name); const card = ruleCardFor(s.name);
                    const handler: Handler | null = info?.handler || null;
                    return (
                      <li key={`${s.id}-${s.name}`} className={`${card ? "has-card" : ""} ${s.prepared ? "prepared" : ""}`} title={[info ? `${info.duration}${info.concentration ? " (C)" : ""}${info.aoe ? ` · ${info.aoe} ${info.shape}` : ""}${info.save ? ` · ${info.save}` : ""}${info.effect ? ` · ${info.effect}` : ""}` : "Not in the inventory (homebrew or not yet listed)", card ? `Rule card: ${card.kind} — ${card.note}` : handler && handler !== "out-of-scope" ? `Handler to write: ${HANDLER_LABEL[handler]}` : ""].filter(Boolean).join("\n")}>
                        <span className="name">{s.name}{s.legacy ? <small className="muted"> (2014)</small> : null}{s.concentration ? <small title="Concentration"> ⓒ</small> : null}{s.ritual ? <small title="Ritual"> ⓡ</small> : null}</span>
                        {card ? <span className="pill card">✓ {card.kind}</span> : handler && handler !== "out-of-scope" ? <span className="pill todo">{HANDLER_LABEL[handler]}</span> : <span className="pill none">{handler ? "—" : "?"}</span>}
                        {s.source !== "class" && <small className="muted">{s.source}</small>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </article>
        ))}
      </div>
      <p className="muted small">✓ = the DM Screen runs it (auras, lights). A handler name = the inventory knows its shape and the handler is on the roadmap (docs/SPELL-RULES.md). — = utility, no automation planned. ? = not in the inventory.</p>
    </section>
  );
}
